import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from '../../RootStore';
import { appointmentService } from '../../../services/modules/appointment/appointment.service';
import { IAppointment, AppointmentFormValues } from '../../../models/appointment/AppointmentModel';
import type { GridToolbarHelpers } from '@inuxe/prime-odata-grid';
import { toast } from 'react-toastify';

interface ODataParams {
  $filter?: string;
  $orderby?: string;
  $skip?: number;
  $top?: number;
  $select?: string;
  $count?: boolean;
}

interface GridParams {
  startRow: number;
  endRow: number;
  filterModel: any;
  sortModel: any;
  oDataFilter?: string;
}

export class AppointmentStore {
  rootStore: RootStore;
  appointmentList: IAppointment[] = [];
  gridHelpers: GridToolbarHelpers<IAppointment> | null = null;
  selectedAppointmentToEdit: IAppointment | null = null;
  appointmentToDelete: IAppointment | null = null;
  selectedAppointmentToView: IAppointment | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setGridHelpers = (helpers: GridToolbarHelpers<IAppointment>) => {
    this.gridHelpers = helpers;
  };

  refreshGrid = () => {
    this.gridHelpers?.refreshGrid();
  };

  refreshGridWithCurrentSettings = () => {
    this.gridHelpers?.refreshGrid();
  };

  listAppointments = async (
    params?: GridParams
  ): Promise<{ rows: IAppointment[]; lastRow: number; count: number }> => {
    try {
      this.rootStore.uiStore.setLoading(true);

      if (params) {
        const odataParams: ODataParams = {
          $count: true,
          $skip: params.startRow,
          $top: params.endRow - params.startRow,
        };

        if (params.oDataFilter) {
          odataParams.$filter = params.oDataFilter;
        } else if (params.filterModel) {
          const filters = Object.entries(params.filterModel).map(
            ([field, filter]: [string, any]) => {
              const numericFields = ['id', 'hospitalId', 'personId'];
              if (numericFields.includes(field)) {
                return `${field} eq ${filter.filter}`;
              }
              if (field === 'appointmentDate') {
                const dateValue = filter.filter;
                if (dateValue) {
                  return `appointmentDate eq ${dateValue}`;
                }
              }
              const value = String(filter.filter ?? '').toLowerCase().replace(/'/g, "''");
              return `contains(tolower(${field}), '${value}')`;
            }
          );

          if (filters.length > 0) {
            odataParams.$filter = filters.join(' and ');
          }
        }

        if (params.sortModel && params.sortModel.length > 0) {
          const { colId, sort } = params.sortModel[0];
          odataParams.$orderby = `${colId} ${sort === 'asc' ? 'asc' : 'desc'}`;
        }

        const response: any = await appointmentService.listAllAppointments({
          params: odataParams,
        });

        return runInAction(() => {
          if (response) {
            const totalCount = response['@odata.count'] || 0;
            const appointments = response.value || [];
            this.appointmentList = appointments;
            return {
              rows: appointments,
              lastRow: totalCount,
              count: totalCount,
            };
          }
          return {
            rows: [],
            lastRow: 0,
            count: 0,
          };
        });
      } else {
        const response: any = await appointmentService.listAllAppointments();

        runInAction(() => {
          this.appointmentList = Array.isArray(response) ? response : [];
        });

        return {
          rows: this.appointmentList,
          lastRow: this.appointmentList.length,
          count: this.appointmentList.length,
        };
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      runInAction(() => {
        this.rootStore.uiStore.setError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        );
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  getAppointmentsByHospitalId = async (hospitalId: number): Promise<IAppointment[]> => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await appointmentService.getAppointmentsByHospitalId(hospitalId);
      return runInAction(() => {
        return Array.isArray(response) ? response : [];
      });
    } catch (error) {
      console.error('Error fetching appointments by hospital ID:', error);
      runInAction(() => {
        this.rootStore.uiStore.setError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        );
      });
      return [];
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  getTodayAppointmentsForUser = async (): Promise<IAppointment[]> => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await appointmentService.getTodayAppointmentsForUser();
      return runInAction(() => {
        return Array.isArray(response) ? response : [];
      });
    } catch (error) {
      console.error('Error fetching today appointments for user:', error);
      runInAction(() => {
        this.rootStore.uiStore.setError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        );
      });
      return [];
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  getAppointmentsByPersonId = async (personId: number): Promise<IAppointment[]> => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await appointmentService.getAppointmentsByPersonId(personId);
      return runInAction(() => {
        return Array.isArray(response) ? response : [];
      });
    } catch (error) {
      console.error('Error fetching appointments by person ID:', error);
      runInAction(() => {
        this.rootStore.uiStore.setError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        );
      });
      return [];
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  createAppointment = async (values: AppointmentFormValues) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await appointmentService.createAppointment(values);

      if (response.error != true) {
        this.refreshGridWithCurrentSettings();
        toast.success('Randevu başarıyla oluşturuldu');
      } else {
        toast.error('Randevu oluşturulamadı. ' + response.message);
      }
      return response;
    } catch (error) {
      console.error('Error creating appointment:', error);
      runInAction(() => {
        this.rootStore.uiStore.setError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        );
        toast.error('Randevu oluşturulurken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  updateAppointment = async (id: number, values: AppointmentFormValues) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await appointmentService.updateAppointment(id, values);

      if (response.error === false) {
        this.refreshGridWithCurrentSettings();
        toast.success('Randevu başarıyla güncellendi');
      } else {
        toast.error('Randevu güncellenemedi. ' + response.message);
      }
      return response;
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Randevu güncellenirken bir hata oluştu');
      throw error;
    } finally {
      this.rootStore.uiStore.setLoading(false);
    }
  };

  deleteAppointment = async (id: number) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await appointmentService.deleteAppointment(id);
      if (response.error == false) {
        toast.success('Randevu başarıyla silindi.');
        this.refreshGridWithCurrentSettings();
      } else {
        toast.error('Randevu silinemedi. ' + response.message);
      }
      return response;
    } catch (error) {
      console.error('Randevu silinirken hata:', error);
      toast.error('Randevu silinirken bir hata oluştu');
      throw error;
    } finally {
      this.rootStore.uiStore.setLoading(false);
    }
  };

  setSelectedAppointmentToEdit = (appointment: IAppointment | null) => {
    this.selectedAppointmentToEdit = appointment;
  };

  fetchAppointmentById = async (id: number): Promise<IAppointment | null> => {
    try {
      const data = await appointmentService.getAppointmentById(id);
      runInAction(() => {
        this.selectedAppointmentToEdit = data;
      });
      return data;
    } catch (error) {
      console.error('Error fetching appointment:', error);
      return null;
    }
  };

  setAppointmentToDelete = (appointment: IAppointment | null) => {
    this.appointmentToDelete = appointment;
  };

  setSelectedAppointmentToView = (appointment: IAppointment | null) => {
    this.selectedAppointmentToView = appointment;
  };
}

